import { MessagingBase } from "./MessagingBase";
/// Message Handler Decorators ///
export const MESSAGE_HANDLER_SYMBOL = Symbol("__messageHandler__");
export function MessageHandler(first, ...rest) {
    return (targetClassInstance, methodName) => {
        var _a;
        assertDecoratorIsUsedWithinMessagingBase(targetClassInstance, methodName);
        const arr = (_a = Reflect.get(targetClassInstance, MESSAGE_HANDLER_SYMBOL)) !== null && _a !== void 0 ? _a : [];
        for (const m of [first, ...rest])
            arr.push([m, methodName]);
        Reflect.set(targetClassInstance, MESSAGE_HANDLER_SYMBOL, arr);
    };
}
export const MESSAGE_REQUEST_HANDLER_SYMBOL = Symbol("__messageRequestHandler__");
export function MessageRequestHandler(first, ...rest) {
    return (targetClassInstance, methodName) => {
        var _a;
        assertDecoratorIsUsedWithinMessagingBase(targetClassInstance, methodName);
        const arr = (_a = Reflect.get(targetClassInstance, MESSAGE_REQUEST_HANDLER_SYMBOL)) !== null && _a !== void 0 ? _a : [];
        for (const m of [first, ...rest])
            arr.push([m, methodName]);
        Reflect.set(targetClassInstance, MESSAGE_REQUEST_HANDLER_SYMBOL, arr);
    };
}
export const MESSAGE_OR_MESSAGE_REQUEST_HANDLER_SYMBOL = Symbol("__messageOrMessageRequestHandler__");
export function MessageOrMessageRequestHandler(first, ...rest) {
    return (targetClassInstance, methodName) => {
        var _a;
        assertDecoratorIsUsedWithinMessagingBase(targetClassInstance, methodName);
        const arr = (_a = Reflect.get(targetClassInstance, MESSAGE_OR_MESSAGE_REQUEST_HANDLER_SYMBOL)) !== null && _a !== void 0 ? _a : [];
        for (const m of [first, ...rest])
            arr.push([m, methodName]);
        Reflect.set(targetClassInstance, MESSAGE_OR_MESSAGE_REQUEST_HANDLER_SYMBOL, arr);
    };
}
/// Facet Handler Decorators ///
export const FACET_HANDLER_SYMBOL = Symbol("__facetHandler__");
export function FacetHandler(firstOrList, ...rest) {
    return (targetClassInstance, methodName) => {
        var _a;
        assertDecoratorIsUsedWithinMessagingBase(targetClassInstance, methodName);
        const types = Array.isArray(firstOrList)
            ? firstOrList
            : [firstOrList, ...rest];
        const arr = (_a = Reflect.get(targetClassInstance, FACET_HANDLER_SYMBOL)) !== null && _a !== void 0 ? _a : [];
        for (const F of types)
            arr.push([F, methodName]);
        Reflect.set(targetClassInstance, FACET_HANDLER_SYMBOL, arr);
    };
}
export const FACET_REMOVE_HANDLER_SYMBOL = Symbol("__facetRemoveHandler__");
export function FacetRemoveHandler(firstOrList, ...rest) {
    return (targetClassInstance, methodName) => {
        var _a;
        assertDecoratorIsUsedWithinMessagingBase(targetClassInstance, methodName);
        const types = Array.isArray(firstOrList)
            ? firstOrList
            : [firstOrList, ...rest];
        const arr = (_a = Reflect.get(targetClassInstance, FACET_REMOVE_HANDLER_SYMBOL)) !== null && _a !== void 0 ? _a : [];
        for (const F of types)
            arr.push([F, methodName]);
        Reflect.set(targetClassInstance, FACET_REMOVE_HANDLER_SYMBOL, arr);
    };
}
/// Model Event Handler Decorators ///
export const MODEL_EVENT_HANDLER_SYMBOL = Symbol("__modelEventHandler__");
// ToDo: Add more types here if needed and extend the switch-case statement in MessagingBase.ts
export var ModelEventType;
(function (ModelEventType) {
    ModelEventType[ModelEventType["MODEL_CREATED"] = 0] = "MODEL_CREATED";
    ModelEventType[ModelEventType["MODEL_DESTROYED"] = 1] = "MODEL_DESTROYED";
    ModelEventType[ModelEventType["FACET_UPDATED"] = 2] = "FACET_UPDATED";
    ModelEventType[ModelEventType["FACET_REMOVED"] = 3] = "FACET_REMOVED";
    ModelEventType[ModelEventType["TRANSFORM_UPDATED"] = 4] = "TRANSFORM_UPDATED";
    ModelEventType[ModelEventType["ROOM_CHANGED"] = 5] = "ROOM_CHANGED";
    ModelEventType[ModelEventType["PARENT_CHANGED"] = 6] = "PARENT_CHANGED";
})(ModelEventType || (ModelEventType = {}));
/**
 * Decorator for simplifying model event handling. The name of the handler function doesn't matter but need to be unique
 * within a module.
 *
 * The number of Parameters depends on the event type. For reference look at the setOn*** Methods in ModelPool. The last
 * parameter to each of these methods is metaData, which can be omitted if not needed.
 *
 * ### ModelEventType.MODEL_CREATED
 * ```ts
 * @ModelEventHandler(ModelEventType.MODEL_CREATED)
 * protected handleMyMessage(model: Model): void {}
 *
 * @ModelEventHandler(ModelEventType.MODEL_CREATED)
 * protected handleMyMessageWithMetaData(model: Model, metaData: ModelMetaData): void {}
 * ```
 *
 * ### ModelEventType.MODEL_DESTROYED
 * ```ts
 * @ModelEventHandler(ModelEventType.MODEL_DESTROYED)
 * protected handleMyMessage(model: Model, destroyPermanently: boolean): void {}
 *
 * @ModelEventHandler(ModelEventType.MODEL_DESTROYED)
 * protected handleMyMessageWithMetaData(model: Model, destroyPermanently: boolean, metaData: ModelMetaData): void {}
 * ```
 *
 * ### ModelEventType.FACET_UPDATED
 * ```ts
 * @ModelEventHandler(ModelEventType.FACET_UPDATED)
 * protected handleMyMessage(model: Model, facet: Facet, created: boolean): void {}
 *
 * @ModelEventHandler(ModelEventType.FACET_UPDATED)
 * protected handleMyMessage(model: Model, facet: Facet, created: boolean, meta: MessageMetaData): void {}
 * ```
 *
 * ### ModelEventType.FACET_REMOVED
 * ```ts
 * @ModelEventHandler(ModelEventType.FACET_REMOVED)
 * protected handleMyMessage(model: Model, facet: Facet): void {}
 *
 * @ModelEventHandler(ModelEventType.FACET_REMOVED)
 * protected handleMyMessage(model: Model, facet: Facet, meta: MessageMetaData): void {}
 * ```
 *
 * ### ModelEventType.TRANSFORM_UPDATED
 * ```ts
 * @ModelEventHandler(ModelEventType.TRANSFORM_UPDATED)
 * protected handleMyMessage(model: Model, localTransform: Matrix4): void {}
 *
 * @ModelEventHandler(ModelEventType.TRANSFORM_UPDATED)
 * protected handleMyMessage(model: Model, localTransform: Matrix4, meta: MessageMetaData): void {}
 * ```
 * Note: The global transformation matrix can be obtained via `model.getWorldTransform()` which is always guaranteed to be
 * up-to-date.
 * ```ts
 * protected handleMyMessage(model: Model, _localTransform: Matrix4): void {
 *  const worldTransform = model.getWorldTransform()
 * }
 * ```
 *
 * ### ModelEventType.ROOM_CHANGED
 * ```ts
 * @ModelEventHandler(ModelEventType.ROOM_CHANGED)
 * protected handleMyMessage(model: Model, room: Model): void {}
 *
 * @ModelEventHandler(ModelEventType.ROOM_CHANGED)
 * protected handleMyMessage(model: Model, room: Model, meta: MessageMetaData): void {}
 * ```
 *
 * ### ModelEventType.PARENT_CHANGED
 * ```ts
 * @ModelEventHandler(ModelEventType.PARENT_CHANGED)
 * protected handleMyMessage(model: Model, parent: Model): void {}
 *
 * @ModelEventHandler(ModelEventType.PARENT_CHANGED)
 * protected handleMyMessage(model: Model, parent: Model, meta: MessageMetaData): void {}
 * ```
 */
export function ModelEventHandler(modelEventType) {
    return (targetClassInstance, methodName) => {
        var _a;
        assertDecoratorIsUsedWithinMessagingBase(targetClassInstance, methodName);
        const arr = (_a = Reflect.get(targetClassInstance, MODEL_EVENT_HANDLER_SYMBOL)) !== null && _a !== void 0 ? _a : [];
        arr.push([modelEventType, methodName]);
        Reflect.set(targetClassInstance, MODEL_EVENT_HANDLER_SYMBOL, arr);
    };
}
const assertDecoratorIsUsedWithinMessagingBase = (target, methodName) => {
    if (!Object.prototype.isPrototypeOf.call(MessagingBase.prototype, target)) {
        const className = target.constructor.name;
        const signature = `${className}.${String(methodName)}`;
        throw new Error(`Decorator on "${signature}" can only be used in classes that extend from "MessagingBase". Make sure ${className} inherits from either "NewModule" or "Service"`);
    }
};
